import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchDropdownData } from 'services/api/fetchFieldData';
import { useFetchContext } from 'services/api/FetchContext';
import { FieldOptionFetchResponse, FieldType } from 'types/FieldTypes';
import FieldsCard from 'components/detailcards/FieldsCard';
import TicketRequesterCard from './TicketRequesterCard';
import TicketLinkedItemsCard from './TicketLinkedItemsCard';
import { Ticket } from './TicketTypes';

interface TicketSidePanelProps {
    ticket: Ticket;
    loadStatus: 'idle' | 'loading' | 'loaded';
    refetchData: () => void;
}

// The side column of a ticket with the editable details card and the requester card
const TicketSidePanel: React.FC<TicketSidePanelProps> = ({ ticket, loadStatus, refetchData }) => {
    const { handleLogout, activeFeatures } = useAuthContext();
    const { getCache, setCache } = useFetchContext();
    const [dropdownData, setDropdownData] = useState<Record<string, FieldOptionFetchResponse>>({});

    // Keep the logout handler in a ref, its identity changes on every render and would refire the fetch effects
    const handleLogoutRef = useRef(handleLogout);
    handleLogoutRef.current = handleLogout;

    // The editable detail fields of the ticket
    const detailFields: FieldType[] = useMemo(() => [
        {
            type: 'dropdown',
            name: 'category',
            label: 'ticket.general.category_label',
            apiRequest: [{
                endpoint: 'get_ticket_category_list',
                object: 'ticket_category',
                params: { deleted: false, is_active: true }
            }],
            disabled_selected: 'ticket.general.category_placeholder',
            allowNoneOption: true,
            showSearch: false,
            openOnEdit: true,
            optionFormat: 'name',
            selectionFormat: 'name',
        },
        {
            type: 'dropdown',
            name: 'division',
            label: 'ticket.general.division_label',
            apiRequest: [{
                endpoint: 'get_division_list',
                object: 'division',
                params: { is_active: true }
            }],
            activeFeature: 'divisions',
            disabled_selected: 'ticket.general.division_placeholder',
            allowNoneOption: false,
            showSearch: false,
            openOnEdit: true,
            optionFormat: 'name',
            selectionFormat: 'name',
        },
        {
            type: 'dropdown',
            name: 'assignee',
            label: 'ticket.general.assignee_label',
            apiRequest: [{
                endpoint: 'get_user_list',
                object: 'user',
                params: { is_active: true, office_access: true }
            }],
            disabled_selected: 'ticket.general.assignee_placeholder',
            selectionFormat: 'full_name',
            optionFormat: 'full_name',
            allowNoneOption: true,
            showSearch: true,
            openOnEdit: true,
        },
    ], []);

    // Fetch the options of the dropdown fields once
    useEffect(() => {
        const loadDropdownData = async () => {
            const data = await fetchDropdownData([{ component: 'FieldsCard', fields: detailFields }], {}, activeFeatures, getCache, setCache, handleLogoutRef.current);
            setDropdownData(data);
        };
        loadDropdownData();
        // eslint-disable-next-line
    }, [detailFields]);

    return (
        <div className='ticket-side'>
            <FieldsCard
                viewKey={`ticket-detail-${ticket.id}`}
                apiObject='ticket'
                itemId={String(ticket.id)}
                title='ticket.general.details_card_title'
                fields={detailFields}
                alignment='stacked'
                data={ticket}
                dropdownData={dropdownData}
                loadStatus={loadStatus}
                refetchData={refetchData}
            />
            <TicketRequesterCard ticket={ticket} loadStatus={loadStatus} />
            <TicketLinkedItemsCard ticket={ticket} />
        </div>
    );
};

export default TicketSidePanel;
